import "../styles/globals.css";
import { useEffect, useState } from "react";
import { useRouter } from "next/router";
import { onAuthStateChanged } from "firebase/auth";
import { AccountProvider, useAccount } from "../lib/accountContext";
import { auth } from "../lib/firebase";

const authExcludedRoutes = ["/login", "/marketing"];
const marketingHosts = ["konnuko.com", "www.konnuko.com"];

function AuthGate({ children }) {
  const router = useRouter();
  const { provisionAndSetAccount } = useAccount();
  const [checking, setChecking] = useState(true);
  const [redirecting, setRedirecting] = useState(false);

  // Skip auth gating on marketing hosts
  const isMarketingHost =
    typeof window !== "undefined" &&
    marketingHosts.includes(window.location.hostname.toLowerCase());
  const bypassAuth =
    isMarketingHost || authExcludedRoutes.includes(router.pathname);

  useEffect(() => {
    if (bypassAuth) {
      setChecking(false);
      setRedirecting(false);
      return undefined;
    }

    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setRedirecting(true);
        if (!authExcludedRoutes.includes(router.pathname)) {
          router.replace("/login").finally(() => {
            setRedirecting(false);
            setChecking(false);
          });
        } else {
          setRedirecting(false);
          setChecking(false);
        }
        return;
      }
      try {
        await provisionAndSetAccount(user.uid, {
          name: user.displayName || "My Workspace",
        });
      } catch (err) {
        console.error("Provisioning error:", err);
      } finally {
        setChecking(false);
        setRedirecting(false);
      }
    });
    return () => unsub();
  }, [bypassAuth, provisionAndSetAccount, router]);

  if (bypassAuth) {
    return children;
  }

  if (checking || redirecting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <p className="text-sm text-slate-600">Loading...</p>
      </div>
    );
  }

  return children;
}

export default function MyApp({ Component, pageProps }) {
  return (
    <AccountProvider>
      <AuthGate>
        <Component {...pageProps} />
      </AuthGate>
    </AccountProvider>
  );
}
